/* eslint-disable etc/no-commented-out-code */
import { Icon } from "@iconify/react";
import { FormikConfig, FormikProps, getIn, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Input } from "../../components/shared/InputField";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import {
  useGetEmailSettings,
  useSetEmailSettings,
} from "../../queries/siteSettings";
import { usePermissionStore } from "../../store/permissionStore";
import {
  EmailSetting,
  SendEsopStatementToEmployeeConfig,
} from "../../types/SiteSettings";
import { GrantState } from "../../types/common";
import { useEsopPlans } from "../../queries";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import Tooltip from "../../components/shared/Tooltip";
import InfoBox from "../../components/shared/InfoBox";

type SubSettingsProps = {
  formik: FormikProps<EmailSetting>;
};
type SettingsRowProps = {
  label: string;
  description?: string;
  value: boolean;
};

const createDefaultEmployeeStatementConfig =
  (): SendEsopStatementToEmployeeConfig => ({
    isEnabled: false,
    deliverySchedule: {
      frequency: "MONTHLY",
      firstDeliveryMonth: null,
      repeatEveryMonths: null,
      runOn: "DAY_OF_MONTH",
      dayOfMonth: 1,
      overflowBehavior: null,
      customIntervalDays: null,
    },
    statementLookbackMonths: 1,
    recipientFilters: { excludeEmployeesWOCredentials: false },
  });

const isCalendarDate = (value: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

const hasValidMonthlyRunDate = (
  deliverySchedule: SendEsopStatementToEmployeeConfig["deliverySchedule"]
) => {
  if (deliverySchedule.runOn === "LAST_DAY_OF_MONTH") {
    return (
      deliverySchedule.dayOfMonth === null &&
      deliverySchedule.overflowBehavior === null
    );
  }

  if (
    deliverySchedule.runOn !== "DAY_OF_MONTH" ||
    !Number.isInteger(deliverySchedule.dayOfMonth) ||
    (deliverySchedule.dayOfMonth as number) < 1 ||
    (deliverySchedule.dayOfMonth as number) > 31
  ) {
    return false;
  }

  return (deliverySchedule.dayOfMonth as number) >= 29
    ? deliverySchedule.overflowBehavior !== null
    : deliverySchedule.overflowBehavior === null;
};

const isValidEmployeeStatementConfig = (
  configuration: SendEsopStatementToEmployeeConfig
) => {
  const { deliverySchedule, statementLookbackMonths } = configuration;
  if (!Number.isInteger(statementLookbackMonths) || statementLookbackMonths < 1)
    return false;

  if (deliverySchedule.frequency === "MONTHLY") {
    return (
      deliverySchedule.firstDeliveryMonth === null &&
      deliverySchedule.repeatEveryMonths === null &&
      deliverySchedule.customIntervalDays === null &&
      hasValidMonthlyRunDate(deliverySchedule)
    );
  }

  if (deliverySchedule.frequency === "INTERVAL") {
    return (
      isCalendarDate(deliverySchedule.firstDeliveryMonth) &&
      Number.isInteger(deliverySchedule.repeatEveryMonths) &&
      (deliverySchedule.repeatEveryMonths as number) >= 1 &&
      (deliverySchedule.repeatEveryMonths as number) <= 12 &&
      deliverySchedule.customIntervalDays === null &&
      hasValidMonthlyRunDate(deliverySchedule)
    );
  }

  return (
    isCalendarDate(deliverySchedule.firstDeliveryMonth) &&
    Number.isInteger(deliverySchedule.customIntervalDays) &&
    (deliverySchedule.customIntervalDays as number) >= 1 &&
    deliverySchedule.repeatEveryMonths === null &&
    deliverySchedule.runOn === null &&
    deliverySchedule.dayOfMonth === null &&
    deliverySchedule.overflowBehavior === null
  );
};

function EmailSettings({ openCategory }: { openCategory?: string }) {
  const {
    data: emailSettingDetails,
    refetch,
    isError,
    isFetched,
  } = useGetEmailSettings();
  const { mutate: setEmailSettings, isLoading: isSettingsSaved } =
    useSetEmailSettings();
  const { permission } = usePermissionStore();
  const isUserEsopViewer = isEsopViewer();
  const initialValues: EmailSetting = {
    emailsToBeSent: emailSettingDetails?.emailsToBeSent || false,
    approverMailToAdmin: emailSettingDetails?.approverMailToAdmin || false,
    communicationEmailId:
      emailSettingDetails?.communicationEmailId == null
        ? "hissa@rulezero.com"
        : emailSettingDetails?.communicationEmailId || "hissa@rulezero.com",
    senderNameForEmail:
      emailSettingDetails?.senderNameForEmail == null
        ? "Hissa"
        : emailSettingDetails?.senderNameForEmail || "Hissa",
    isAttachmentEnabled: emailSettingDetails?.isAttachmentEnabled ?? false,
    isAttachmentEnabledForAdmins:
      emailSettingDetails?.isAttachmentEnabledForAdmins ?? false,
    reminderEmailsToBeSent:
      emailSettingDetails?.reminderEmailsToBeSent || false,
    reminderEmailsStartFrom: emailSettingDetails?.reminderEmailsStartFrom || 0,
    reminderEmailsInterval: emailSettingDetails?.reminderEmailsInterval || 0,
    employeeInactivityLogout:
      emailSettingDetails?.employeeInactivityLogout || false,
    sendEmailsToEmployees: emailSettingDetails?.sendEmailsToEmployees || false,
    sendGrantEmailsToEmployees:
      emailSettingDetails?.sendGrantEmailsToEmployees || false,
    sendVestingEmailsToEmployees:
      emailSettingDetails?.sendVestingEmailsToEmployees || false,
    sendBuyBackEmailsToEmployees:
      emailSettingDetails?.sendBuyBackEmailsToEmployees || false,
    sendGrantOfferedEmailsToEmployees:
      emailSettingDetails?.sendGrantOfferedEmailsToEmployees || false,
    generateCredentialsOnGrantOffer:
      emailSettingDetails?.generateCredentialsOnGrantOffer || false,
    sendGrantWithdrawEmailsToEmployees:
      emailSettingDetails?.sendGrantWithdrawEmailsToEmployees || false,
    sendGrantReminderEmailsToEmployees:
      emailSettingDetails?.sendGrantReminderEmailsToEmployees || false,
    grantReminderStartFromEmployee:
      emailSettingDetails?.grantReminderStartFromEmployee || 0,
    grantReminderIntervalEmployee:
      emailSettingDetails?.grantReminderIntervalEmployee || 0,
    sendVestingCompleteEmailsToEmployees:
      emailSettingDetails?.sendVestingCompleteEmailsToEmployees || false,
    sendBuyBackOfferedEmailsToEmployees:
      emailSettingDetails?.sendBuyBackOfferedEmailsToEmployees || false,
    sendBuyBackClosedEmailsToEmployees:
      emailSettingDetails?.sendBuyBackClosedEmailsToEmployees || false,
    sendForfeitureLeterToEmployees:
      emailSettingDetails?.sendForfeitureLeterToEmployees || false,
    sendBuyBackReminderEmailsToEmployees:
      emailSettingDetails?.sendBuyBackReminderEmailsToEmployees || false,
    sendFullAndFinalLetterToEmployees:
      emailSettingDetails?.sendFullAndFinalLetterToEmployees || false,
    sendEmailsToAdmin: emailSettingDetails?.sendEmailsToAdmin || false,
    sendGrantEmailsToAdmin:
      emailSettingDetails?.sendGrantEmailsToAdmin || false,
    sendGrantApprovalEmailsToAdmin:
      emailSettingDetails?.sendGrantApprovalEmailsToAdmin || false,
    grantApprovalEmailActions: {
      enabled: emailSettingDetails?.grantApprovalEmailActions?.enabled ?? false,
      tokenTtlHours:
        emailSettingDetails?.grantApprovalEmailActions?.tokenTtlHours ?? 72,
    },
    sendGrantApprovalRequestEmailToApprover:
      emailSettingDetails?.sendGrantApprovalRequestEmailToApprover || false,
    sendPlanEmailsToAdmin: emailSettingDetails?.sendPlanEmailsToAdmin || false,
    sendTransactionEmailsToAdmin:
      emailSettingDetails?.sendTransactionEmailsToAdmin || false,
    sendResignationReminderEmailsToAdmin:
      emailSettingDetails?.sendResignationReminderEmailsToAdmin || false,
    sendOptionsExpiryReminderEmailsToEmployee:
      emailSettingDetails?.sendOptionsExpiryReminderEmailsToEmployee || false,
    optionsExpiryReminderStartFromEmployee:
      emailSettingDetails?.optionsExpiryReminderStartFromEmployee || 0,
    optionsExpiryReminderIntervalEmployee:
      emailSettingDetails?.optionsExpiryReminderIntervalEmployee || 0,
    sendEmployeeDataSyncRecords:
      emailSettingDetails?.sendEmployeeDataSyncRecords || false,
    sendGrantAcceptedEmailsToEmployees:
      emailSettingDetails?.sendGrantAcceptedEmailsToEmployees || false,
    sendGrantAcceptedEmailsToAdmin:
      emailSettingDetails?.sendGrantAcceptedEmailsToAdmin || false,
    sendZeroVestingEmailsToEmployees:
      emailSettingDetails?.sendZeroVestingEmailsToEmployees || false,
    sendPendingResignationSummaryEmailsToAdmin:
      emailSettingDetails?.sendPendingResignationSummaryEmailsToAdmin || false,
    pendingResignationInclusionOffset:
      emailSettingDetails?.pendingResignationInclusionOffset || 0,
    pendingResignationReminderInterval:
      emailSettingDetails?.pendingResignationReminderInterval || 0,
    sendOptionsExpiryReminderEmailsToAdmin:
      emailSettingDetails?.sendOptionsExpiryReminderEmailsToAdmin || false,
    optionsExpiryReminderStartFromAdmin:
      emailSettingDetails?.optionsExpiryReminderStartFromAdmin || 0,
    optionsExpiryReminderIntervalAdmin:
      emailSettingDetails?.optionsExpiryReminderIntervalAdmin || 0,
    sendMilestoneCompletionReminderEmailsToAdmin:
      emailSettingDetails?.sendMilestoneCompletionReminderEmailsToAdmin ||
      false,
    sendMilestoneReminderEmailsToAdmin:
      emailSettingDetails?.sendMilestoneReminderEmailsToAdmin || false,
    milestoneReminderStartFromAdmin:
      emailSettingDetails?.milestoneReminderStartFromAdmin || 0,
    milestoneReminderIntervalAdmin:
      emailSettingDetails?.milestoneReminderIntervalAdmin || 0,
    grantDateReminder: emailSettingDetails?.grantDateReminder ?? {
      enabled: false,
      recipientRoles: ["ADMIN"],
      pre: { enabled: true, startFromDays: undefined, intervalDays: undefined },
      due: { enabled: true },
      post: { enabled: false, startFromDays: 1, intervalDays: undefined },
    },
    sendMonthlyStatement: emailSettingDetails?.sendMonthlyStatement || false,
    monthlyStatementInterval:
      emailSettingDetails?.monthlyStatementInterval || 1,
    sendEsopStatementToEmployeeConfig:
      emailSettingDetails?.sendEsopStatementToEmployeeConfig ??
      createDefaultEmployeeStatementConfig(),
    emailRecipientRoles: emailSettingDetails?.emailRecipientRoles || [],
    sendEmailsToManagers: emailSettingDetails?.sendEmailsToManagers || false,
    sendManagerPortalAccessEmailToManagers:
      emailSettingDetails?.sendManagerPortalAccessEmailToManagers || false,
    sendManagerEmployeeAssignedEmailToManagers:
      emailSettingDetails?.sendManagerEmployeeAssignedEmailToManagers || false,
    sendManagerEmployeeRemovedEmailToManagers:
      emailSettingDetails?.sendManagerEmployeeRemovedEmailToManagers || false,
  };

  const [initialSettings, setInitialSettings] = useState<EmailSetting>();

  const initialSettingsSet = useRef(false);

  useEffect(() => {
    if (emailSettingDetails) {
      formik.setValues({
        ...initialValues,
        ...emailSettingDetails,
        sendEsopStatementToEmployeeConfig:
          emailSettingDetails.sendEsopStatementToEmployeeConfig ??
          initialValues.sendEsopStatementToEmployeeConfig,
      });
      if (!initialSettingsSet.current) {
        setInitialSettings(_.cloneDeep(initialValues));
        initialSettingsSet.current = true;
      }
    }
  }, [emailSettingDetails]);
  const tabs = [
    { name: "General", isDisabled: false },
    { name: "Admin", isDisabled: false },
    { name: "Employee", isDisabled: false },
    { name: "Manager", isDisabled: false },
  ];
  const [handleChanges, setHandleChanges] = useState<boolean>();
  const validationSchema = Yup.object({
    emailsToBeSent: Yup.boolean(),
    communicationEmailId: Yup.string().when(["emailsToBeSent"], {
      is: (emailsToBeSent: boolean) => emailsToBeSent === true,
      then: Yup.string().email().required("Valid Email is required"),
    }),
    senderNameForEmail: Yup.string().when(["emailsToBeSent"], {
      is: (emailsToBeSent: boolean) => emailsToBeSent === true,
      then: Yup.string()
        .required("Email Sender Name is required")
        .min(1, "minimum one character required")
        .max(100),
    }),
    reminderEmailsToBeSent: Yup.boolean().required(),
    reminderEmailsStartFrom: Yup.number().when(["reminderEmailsToBeSent"], {
      is: (reminderEmailsToBeSent: boolean) => reminderEmailsToBeSent === true,
      then: Yup.number()
        .positive("required")
        .required("required")
        .typeError("expected a number"),
    }),
    reminderEmailsInterval: Yup.number().when(["reminderEmailsToBeSent"], {
      is: (reminderEmailsToBeSent: boolean) => reminderEmailsToBeSent === true,
      then: Yup.number()
        .positive("required")
        .required("required")
        .typeError("expected a number"),
    }),
    employeeInactivityLogout: Yup.boolean().required(),
    approverMailToAdmin: Yup.boolean(),
    isAttachmentEnabled: Yup.boolean(),
    sendGrantEmailsToEmployees: Yup.boolean().required(),
    sendVestingEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackEmailsToEmployees: Yup.boolean().required(),
    sendGrantOfferedEmailsToEmployees: Yup.boolean().required(),
    generateCredentialsOnGrantOffer: Yup.boolean().required(),
    sendGrantWithdrawEmailsToEmployees: Yup.boolean().required(),
    sendGrantReminderEmailsToEmployees: Yup.boolean().required(),
    grantReminderStartFromEmployee: Yup.number().when(
      ["sendGrantReminderEmailsToEmployees"],
      {
        is: (sendGrantReminderEmailsToEmployees: boolean) =>
          sendGrantReminderEmailsToEmployees === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .moreThan(0)
          .typeError("expected a number")
          .label("Start reminders (days after grant offer)"),
      }
    ),
    grantReminderIntervalEmployee: Yup.number().when(
      ["sendGrantReminderEmailsToEmployees"],
      {
        is: (sendGrantReminderEmailsToEmployees: boolean) =>
          sendGrantReminderEmailsToEmployees === true,
        then: Yup.number()
          .min(1, "Value must be 1 or greater")
          .typeError("expected a number"),
      }
    ),
    sendVestingCompleteEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackOfferedEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackClosedEmailsToEmployees: Yup.boolean().required(),
    sendForfeitureLeterToEmployees: Yup.boolean().when(
      ["sendBuyBackClosedEmailsToEmployees"],
      {
        is: (sendBuyBackClosedEmailsToEmployees: boolean) =>
          sendBuyBackClosedEmailsToEmployees === true,
        then: Yup.boolean(),
      }
    ),
    sendBuyBackReminderEmailsToEmployees: Yup.boolean().required(),
    sendFullAndFinalLetterToEmployees: Yup.boolean().required(),
    sendEmailsToAdmin: Yup.boolean().required(),
    sendGrantEmailsToAdmin: Yup.boolean().required(),
    sendGrantApprovalEmailsToAdmin: Yup.boolean().required(),
    grantApprovalEmailActions: Yup.object({
      enabled: Yup.boolean().required(),
      tokenTtlHours: Yup.number()
        .oneOf([6, 12, 24, 48, 72, 96, 120], "Select a valid expiry")
        .required("Expiry is required")
        .typeError("Expected a number"),
    }).required(),
    sendGrantApprovalRequestEmailToApprover: Yup.boolean().required(),
    sendPlanEmailsToAdmin: Yup.boolean().required(),
    sendTransactionEmailsToAdmin: Yup.boolean().required(),
    sendResignationReminderEmailsToAdmin: Yup.boolean(),
    sendOptionsExpiryReminderEmailsToEmployee: Yup.boolean(),
    optionsExpiryReminderStartFromEmployee: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToEmployee"],
      {
        is: (sendOptionsExpiryReminderEmailsToEmployee: boolean) =>
          sendOptionsExpiryReminderEmailsToEmployee === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .moreThan(0)
          .typeError("expected a number")
          .label("Start reminders (days before expiry)"),
      }
    ),
    optionsExpiryReminderIntervalEmployee: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToEmployee"],
      {
        is: (sendOptionsExpiryReminderEmailsToEmployee: boolean) =>
          sendOptionsExpiryReminderEmailsToEmployee === true,
        then: Yup.number()
          .min(1, "Value must be 1 or greater")
          .typeError("expected a number"),
      }
    ),
    sendEmployeeDataSyncRecords: Yup.boolean(),
    sendGrantAcceptedEmailsToEmployees: Yup.boolean(),
    sendMonthlyStatement: Yup.boolean().required(),
    monthlyStatementInterval: Yup.number().when(["sendMonthlyStatement"], {
      is: (sendMonthlyStatement: boolean) => sendMonthlyStatement === true,
      then: Yup.number()
        .min(1, "Minimum interval is 1 month")
        .max(12, "Maximum interval is 12 months")
        .required("required")
        .typeError("expected a number"),
    }),
    sendEsopStatementToEmployeeConfig: Yup.object().test(
      "valid-employee-statement-schedule",
      "Complete the required statement schedule fields and clear fields that do not apply.",
      (configuration) =>
        Boolean(configuration) &&
        isValidEmployeeStatementConfig(
          configuration as unknown as SendEsopStatementToEmployeeConfig
        )
    ),
    emailRecipientRoles: Yup.array().of(Yup.string()),
    sendPendingResignationSummaryEmailsToAdmin: Yup.boolean().required(),
    pendingResignationInclusionOffset: Yup.number().when(
      ["sendPendingResignationSummaryEmailsToAdmin"],
      {
        is: (sendPendingResignationSummaryEmailsToAdmin: boolean) =>
          sendPendingResignationSummaryEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    pendingResignationReminderInterval: Yup.number().when(
      ["sendPendingResignationSummaryEmailsToAdmin"],
      {
        is: (sendPendingResignationSummaryEmailsToAdmin: boolean) =>
          sendPendingResignationSummaryEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    sendOptionsExpiryReminderEmailsToAdmin: Yup.boolean().required(),
    optionsExpiryReminderStartFromAdmin: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToAdmin"],
      {
        is: (sendOptionsExpiryReminderEmailsToAdmin: boolean) =>
          sendOptionsExpiryReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    optionsExpiryReminderIntervalAdmin: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToAdmin"],
      {
        is: (sendOptionsExpiryReminderEmailsToAdmin: boolean) =>
          sendOptionsExpiryReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    sendMilestoneReminderEmailsToAdmin: Yup.boolean().required(),
    milestoneReminderStartFromAdmin: Yup.number().when(
      ["sendMilestoneReminderEmailsToAdmin"],
      {
        is: (sendMilestoneReminderEmailsToAdmin: boolean) =>
          sendMilestoneReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    milestoneReminderIntervalAdmin: Yup.number().when(
      ["sendMilestoneReminderEmailsToAdmin"],
      {
        is: (sendMilestoneReminderEmailsToAdmin: boolean) =>
          sendMilestoneReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    grantDateReminder: Yup.object({
      enabled: Yup.boolean().required(),
      recipientRoles: Yup.array().of(Yup.string()),
      pre: Yup.object({
        enabled: Yup.boolean().required(),
        startFromDays: Yup.number(),
        intervalDays: Yup.number(),
      }),
      due: Yup.object({ enabled: Yup.boolean().required() }),
      post: Yup.object({
        enabled: Yup.boolean().required(),
        startFromDays: Yup.number(),
        intervalDays: Yup.number(),
      }),
    }).test(
      "grant-date-reminder-settings",
      "Invalid grant date reminder settings",
      function validateGrantDateReminder(configuration) {
        if (!configuration?.enabled) return true;
        if (!configuration.recipientRoles?.length) {
          return this.createError({
            path: "grantDateReminder.recipientRoles",
            message: "Select at least one recipient role",
          });
        }
        for (const [phaseName, phase] of [
          ["pre", configuration.pre],
          ["post", configuration.post],
        ] as const) {
          if (
            phase?.enabled &&
            (!Number.isInteger(phase.startFromDays) ||
              !Number.isInteger(phase.intervalDays) ||
              (phase.startFromDays as number) <= 0 ||
              (phase.intervalDays as number) <= 0)
          ) {
            return this.createError({
              path: `grantDateReminder.${phaseName}.startFromDays`,
              message: "Start and interval must be positive whole days",
            });
          }
        }
        return true;
      }
    ),
    sendEmailsToManagers: Yup.boolean().required(),
    sendManagerPortalAccessEmailToManagers: Yup.boolean().required(),
    sendManagerEmployeeAssignedEmailToManagers: Yup.boolean().required(),
    sendManagerEmployeeRemovedEmailToManagers: Yup.boolean().required(),
  });

  function handleOnCancel(values: EmailSetting) {
    values = initialValues;
  }
  const handleSubmit = (values: EmailSetting) => {
    // fix later to avoid any
    const changedValues: any = {};
    Object.keys(values).forEach((key) => {
      const originalKey: keyof EmailSetting = key as keyof EmailSetting;
      if (values[originalKey] !== initialValues[originalKey]) {
        changedValues[originalKey] = values[originalKey];
      }
    });
    setEmailSettings(changedValues, {
      onSuccess: () => {
        toast("Saved Successfully!", {
          type: "success",
          autoClose: 2000,
        });
      },
      onError: (err: any) => {
        toast(err.response.data.errorRaw, { type: "error", autoClose: 2000 });
      },
    });
  };
  const formik = useFormik<EmailSetting>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      handleSubmit(values);
    },
    onReset: (values) => {
      handleOnCancel(values);
    },
  });
  const allTabs = useMemo(() => {
    const isTabDisabled = (tabName: string) => {
      tabName = tabName.toLowerCase();
      if (tabName === "general") return false;
      if (
        formik.values.emailsToBeSent === false &&
        (tabName === "admin" || tabName === "employee" || tabName === "manager")
      )
        return true;
      return false;
    };
    return tabs.map((tab) => ({ ...tab, isDisabled: isTabDisabled(tab.name) }));
  }, [formik.values.emailsToBeSent]);
  const [activeTab, setActiveTab] = useState<{
    name: string;
    isDisabled: boolean;
  }>(allTabs[0]);

  useEffect(() => {
    if (openCategory) {
      const found = allTabs.find(
        (t) => t.name.toLowerCase() === openCategory.toLowerCase()
      );
      if (found && !found.isDisabled) setActiveTab(found);
    }
  }, [openCategory]);

  useEffect(() => {
    const hasChanges = _.isEqual(emailSettingDetails, formik.values);

    setHandleChanges(hasChanges);
  }, [initialSettings, formik.values]);

  const { values, errors, touched, getFieldProps, setFieldValue } = formik;
  const emailSettingsErrors =
    errors.communicationEmailId ||
    errors.senderNameForEmail ||
    errors.emailsToBeSent ||
    errors.reminderEmailsToBeSent ||
    errors.reminderEmailsStartFrom ||
    errors.reminderEmailsInterval ||
    errors.employeeInactivityLogout ||
    errors.approverMailToAdmin ||
    errors.isAttachmentEnabled;
  function doPartialTouch() {
    formik.setFieldTouched("communicationEmailId");
    formik.setFieldTouched("senderNameForEmail");
    formik.setFieldTouched("emailsToBeSent");
    formik.setFieldTouched("reminderEmailsToBeSent");
    formik.setFieldTouched("reminderEmailsStartFrom");
    formik.setFieldTouched("reminderEmailsInterval");
    formik.setFieldTouched("employeeInactivityLogout");
    formik.setFieldTouched("approverMailToAdmin");
    formik.setFieldTouched("isAttachmentEnabled");
    formik.setFieldTouched("grantApprovalEmailActions.enabled");
    formik.setFieldTouched("grantApprovalEmailActions.tokenTtlHours");
  }

  return (
    <form key="emailSettings" onSubmit={formik.handleSubmit}>
      <Box className="p-4 bg-white rounded-lg w-full">
        <Box className="">
          <VStack className="justify-between p-4 gap-4">
            <Box className="items-center mt-6  text-gray-dark">
              <SettingHeader
                title="Email Settings"
                description="Configure your email settings"
              />
            </Box>
            <HStack className="gap-8 mb-0 border-t-2 border-dotted flex-center">
              {allTabs.map((tab) => (
                <Box
                  onClick={() => {
                    if (!tab.isDisabled) setActiveTab(tab);
                  }}
                  key={tab.name}
                  className={`text-xs3 font-semibold py-4  ${
                    tab.isDisabled
                      ? "text-blue-400 cursor-not-allowed"
                      : "cursor-pointer"
                  } ${
                    tab.name === activeTab.name
                      ? "border-b-2 border-solid border-red-500 text-red-500"
                      : "text-gray-201 "
                  }`}
                >
                  {tab.name}
                </Box>
              ))}
            </HStack>
            <VStack className="max-h-[450px] overflow-auto p-4 w-200">
              {activeTab.name === "General" && (
                <GeneralEmailSettings formik={formik} />
              )}
              {activeTab.name === "Admin" && (
                <AdminEmailSettings formik={formik} />
              )}
              {activeTab.name === "Employee" && (
                <EmployeeEmailSettings formik={formik} />
              )}
              {activeTab.name === "Manager" && (
                <ManagerEmailSettings formik={formik} />
              )}
            </VStack>
          </VStack>
        </Box>

        {!isUserEsopViewer && (
          <HStack className="justify-between mt-12">
            <ButtonPrimary1 type="reset" className="mr-8 text-red-500">
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              className={`${
                !handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
                  ? " cursor-not-allowed"
                  : ""
              } flex items-center self-end justify-center`}
              disabled={
                isError ||
                handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
              }
              type="submit"
              loading={isSettingsSaved}
            >
              Save Settings
            </ButtonPrimary>
          </HStack>
        )}
      </Box>
    </form>
  );
}

const GeneralEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;
  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          <SettingsRow
            id="email-emailsToBeSent"
            label="Allow Notification Emails"
            description="Allow hissa to send notification emails from the platform, this acts a flag to turn on/off all types of notification emails."
            name="emailsToBeSent"
            type="toggle"
            value={formik.values.emailsToBeSent}
            onToggle={() => {
              formik.setFieldValue(
                "emailsToBeSent",
                !formik.values.emailsToBeSent
              );
            }}
          />
          <SettingsRow
            id="email-sendGrantApprovalRequestEmailToApprover"
            label="Grant Approval Sign-off Request Emails"
            description="Email whoever needs to sign off on a grant, whether an approver or a manager, when it's their turn, and let them know if their suggested change is accepted or rejected."
            name="sendGrantApprovalRequestEmailToApprover"
            type="toggle"
            getFieldProps={getFieldProps}
            value={formik.values.sendGrantApprovalRequestEmailToApprover}
            onToggle={() => {
              formik.setFieldValue(
                "sendGrantApprovalRequestEmailToApprover",
                !formik.values.sendGrantApprovalRequestEmailToApprover
              );
            }}
            disabled={!values.emailsToBeSent}
          />
          <SettingsRow
            id="email-communicationEmailId"
            label="Email Sender Address"
            description={`The email address that will be used to send emails, an invalid email will cause the emails to bounce.`}
            type="input"
            inputType="email"
            value={values.communicationEmailId}
            name="communicationEmailId"
            getFieldProps={getFieldProps}
            error={errors?.communicationEmailId}
            required={values.emailsToBeSent}
            // error={touched?.communicationEmailId && errors?.communicationEmailId}
            disabled={!values.emailsToBeSent}
          />
          <SettingsRow
            id="email-senderNameForEmail"
            label="Email Sender Name"
            description={`The name that will be used to send emails`}
            type="input"
            inputType="text"
            value={values.senderNameForEmail}
            name="senderNameForEmail"
            getFieldProps={getFieldProps}
            error={errors?.senderNameForEmail}
            required={values.emailsToBeSent}
            // error={touched?.senderNameForEmail && errors?.senderNameForEmail}
            disabled={!values.emailsToBeSent}
          />
          <SettingsRow
            id="email-employeeInactivityLogout"
            label="Allow admin to deactivate inactive Employees Accounts"
            description={`Allow admin to deactivate inactive Employees Accounts`}
            name="employeeInactivityLogout"
            getFieldProps={getFieldProps}
            value={formik.values.employeeInactivityLogout}
            onToggle={() => {
              formik.setFieldValue(
                "employeeInactivityLogout",
                !formik.values.employeeInactivityLogout
              );
            }}
            error={errors?.employeeInactivityLogout}
            type="toggle"
          />
          <SettingsRow
            id="email-reminderEmailsToBeSent"
            label="Buyback Offer Reminder Emails"
            name="reminderEmailsToBeSent"
            description={`Allow Hissa to send periodic reminder emails to employees to accept their pending buyback offer.`}
            getFieldProps={getFieldProps}
            value={formik.values.reminderEmailsToBeSent}
            onToggle={() => {
              formik.setFieldValue(
                "reminderEmailsToBeSent",
                !formik.values.reminderEmailsToBeSent
              );
            }}
            error={
              formik.touched.reminderEmailsToBeSent
                ? errors?.reminderEmailsToBeSent
                : undefined
            }
            type="toggle"
          />
          <VStack
            className={`gap-4 pl-10 ${
              formik.values.reminderEmailsToBeSent
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <SettingsRow
              id="email-reminderEmailsStartFrom"
              label="Start reminder Emails after"
              value={formik.values.reminderEmailsStartFrom}
              getFieldProps={getFieldProps}
              error={
                formik.touched.reminderEmailsStartFrom
                  ? errors?.reminderEmailsStartFrom
                  : undefined
              }
              name="reminderEmailsStartFrom"
              description={`Number of days after which the reminder emails should start`}
              type="input"
              inputType="number"
              disabled={!formik.values.reminderEmailsToBeSent}
              required={formik.values.reminderEmailsToBeSent}
            />
            <SettingsRow
              id="email-reminderEmailsInterval"
              label="Set reminder Emails interval to"
              value={formik.values.reminderEmailsInterval}
              getFieldProps={getFieldProps}
              error={
                formik.touched.reminderEmailsInterval
                  ? errors?.reminderEmailsInterval
                  : undefined
              }
              name="reminderEmailsInterval"
              description={`How frequently the reminder emails should be sent (in days)`}
              type="input"
              inputType="number"
              disabled={!formik.values.reminderEmailsToBeSent}
              required={formik.values.reminderEmailsToBeSent}
            />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};
const AdminEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;

  // Roles with both display text and enum values
  const emailRecipientRoles = [
    { label: "Admin", value: "ADMIN" },
    { label: "Admin Viewer", value: "ADMIN_VIEWER" },
    { label: "Approver", value: "APPROVER" },
    { label: "Creator", value: "CREATOR" },
  ];
  const upcomingGrantDateRecipientRoles = [
    ...emailRecipientRoles,
    { label: "Manager", value: "MANAGER" },
  ];

  // Reverse lookup: label -> value
  const labelToValue = Object.fromEntries(
    emailRecipientRoles.map((role) => [role.label, role.value])
  );

  // Reverse lookup: value -> label
  const valueToLabel = Object.fromEntries(
    emailRecipientRoles.map((role) => [role.value, role.label])
  );
  const upcomingGrantDateLabelToValue = Object.fromEntries(
    upcomingGrantDateRecipientRoles.map((role) => [role.label, role.value])
  );
  const upcomingGrantDateValueToLabel = Object.fromEntries(
    upcomingGrantDateRecipientRoles.map((role) => [role.value, role.label])
  );

  return (
    <>
      <HStack className="pb-6">
        <VStack className=" gap-10">
          <SettingsRow
            id="email-sendEmailsToAdmin"
            label="Allow Hissa to send notification Emails to Admins"
            description="Allow Hissa to send notification Emails to Admins, this also controls if any emails are sent to admins."
            name="sendEmailsToAdmin"
            type="toggle"
            getFieldProps={getFieldProps}
            value={formik.values.sendEmailsToAdmin}
            onToggle={() => {
              formik.setFieldValue(
                "sendEmailsToAdmin",
                !formik.values.sendEmailsToAdmin
              );
            }}
          />
          <VStack
            className={`justify-between gap-5 pl-10 ${
              formik.values.sendEmailsToAdmin
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <SettingsRow
              id="email-approverMailToAdmin"
              label="Allow admin to receive all approver Emails"
              description="Enable this option to allow admins to receive all approver emails."
              value={formik.values.approverMailToAdmin}
              getFieldProps={getFieldProps}
              type="toggle"
              onToggle={() => {
                formik.setFieldValue(
                  "approverMailToAdmin",
                  !formik.values.approverMailToAdmin
                );
              }}
              name="approverMailToAdmin"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendGrantEmailsToAdmin"
              label="Grant related Emails"
              description="Enable this option to allow Hissa to send grant related emails to admins."
              name="sendGrantEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendGrantEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendGrantEmailsToAdmin",
                  !formik.values.sendGrantEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`justify-between gap-5 pl-10 ${
                formik.values.sendGrantEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-isAttachmentEnabledForAdmins"
                label="Allow Admins to receive attachments in the Approval Emails"
                description="Enable this option to allow admins to receive attachments in the approval emails."
                name="isAttachmentEnabledForAdmins"
                type="toggle"
                disabled={
                  !formik.values.sendGrantEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                getFieldProps={getFieldProps}
                value={formik.values.isAttachmentEnabledForAdmins}
                onToggle={() => {
                  formik.setFieldValue(
                    "isAttachmentEnabledForAdmins",
                    !formik.values.isAttachmentEnabledForAdmins
                  );
                }}
              />
              <SettingsRow
                id="email-sendGrantAcceptedEmailsToAdmin"
                label="Allow Admins to get signed grant attached emails"
                description="Enable this option to allow admins to get signed grant attached emails"
                name="sendGrantAcceptedEmailsToAdmin"
                type="toggle"
                getFieldProps={getFieldProps}
                value={formik.values.sendGrantAcceptedEmailsToAdmin}
                onToggle={() => {
                  formik.setFieldValue(
                    "sendGrantAcceptedEmailsToAdmin",
                    !formik.values.sendGrantAcceptedEmailsToAdmin
                  );
                }}
                disabled={
                  !formik.values.sendGrantEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
              />
              <SettingsRow
                id="email-grantApprovalEmailActions-enabled"
                label="Allow actions in grant approval emails"
                description="Let eligible admins approve or reject the whole grant batch from AMP-enabled Gmail or Outlook emails. This is available only when approver signature is disabled."
                name="grantApprovalEmailActions.enabled"
                type="toggle"
                getFieldProps={getFieldProps}
                value={
                  formik.values.grantApprovalEmailActions?.enabled ?? false
                }
                onToggle={() => {
                  formik.setFieldValue(
                    "grantApprovalEmailActions.enabled",
                    !(formik.values.grantApprovalEmailActions?.enabled ?? false)
                  );
                }}
                disabled={
                  !formik.values.emailsToBeSent ||
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.sendGrantEmailsToAdmin
                }
              />
              <SettingsRow
                id="email-grantApprovalEmailActions-tokenTtlHours"
                label="Email action expiry"
                description="How long approve/reject actions remain valid."
                name="grantApprovalEmailActions.tokenTtlHours"
                type="select"
                value={
                  formik.values.grantApprovalEmailActions?.tokenTtlHours ?? 72
                }
                options={[6, 12, 24, 48, 72, 96, 120].map((hours) => ({
                  label: `${hours} hours`,
                  value: hours,
                }))}
                textGetter={(option: { label: string }) => option.label}
                valueGetter={(option: { value: number }) => option.value}
                onChange={([hours]) =>
                  formik.setFieldValue(
                    "grantApprovalEmailActions.tokenTtlHours",
                    Number(hours)
                  )
                }
                getFieldProps={getFieldProps}
                error={
                  getIn(
                    formik.touched,
                    "grantApprovalEmailActions.tokenTtlHours"
                  )
                    ? getIn(errors, "grantApprovalEmailActions.tokenTtlHours")
                    : undefined
                }
                disabled={
                  !formik.values.grantApprovalEmailActions?.enabled ||
                  !formik.values.emailsToBeSent ||
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.sendGrantEmailsToAdmin
                }
                required={formik.values.grantApprovalEmailActions?.enabled}
              />
            </VStack>
            <SettingsRow
              id="email-sendGrantApprovalEmailsToAdmin"
              label="Grant Approval related Emails"
              description="Notify admins on grant sign-off events: approved, rejected, auto-approved, auto-rejected, ready for approval, or a recommended change."
              name="sendGrantApprovalEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendGrantApprovalEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendGrantApprovalEmailsToAdmin",
                  !formik.values.sendGrantApprovalEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendPlanEmailsToAdmin"
              label="Plan related Emails"
              description="Enable this option to allow Hissa to send plan related emails to admins."
              name="sendPlanEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendPlanEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendPlanEmailsToAdmin",
                  !formik.values.sendPlanEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-milestoneCompletionReminderEmailsToAdmin"
              label="Milestone Completion Reminder Emails"
              description="Enable this option to allow Hissa to send milestone completion reminder emails to admins."
              name="sendMilestoneCompletionReminderEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={
                formik.values.sendMilestoneCompletionReminderEmailsToAdmin!
              }
              onToggle={() => {
                formik.setFieldValue(
                  "sendMilestoneCompletionReminderEmailsToAdmin",
                  !formik.values.sendMilestoneCompletionReminderEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendMilestoneReminderEmailsToAdmin"
              label="Milestone Reminder Emails"
              name="sendMilestoneReminderEmailsToAdmin"
              description={`Send periodic reminder emails to admins for grants nearing their milestone target date.`}
              getFieldProps={getFieldProps}
              value={formik.values.sendMilestoneReminderEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendMilestoneReminderEmailsToAdmin",
                  !formik.values.sendMilestoneReminderEmailsToAdmin
                );
              }}
              error={
                formik.touched.sendMilestoneReminderEmailsToAdmin
                  ? errors?.sendMilestoneReminderEmailsToAdmin
                  : undefined
              }
              type="toggle"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendMilestoneReminderEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-milestoneReminderStartFromAdmin"
                label="Start Reminders (Days Before Target Date)"
                value={formik.values.milestoneReminderStartFromAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.milestoneReminderStartFromAdmin
                    ? errors?.milestoneReminderStartFromAdmin
                    : undefined
                }
                name="milestoneReminderStartFromAdmin"
                description={`Number of days before the milestone target date when reminders should start`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendMilestoneReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                required={formik.values.sendMilestoneReminderEmailsToAdmin}
              />
              <SettingsRow
                id="email-milestoneReminderIntervalAdmin"
                label="Reminder Interval"
                value={formik.values.milestoneReminderIntervalAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.milestoneReminderIntervalAdmin
                    ? errors?.milestoneReminderIntervalAdmin
                    : undefined
                }
                name="milestoneReminderIntervalAdmin"
                description={`How frequently the reminder emails should be sent (in days).`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendMilestoneReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                required={formik.values.sendMilestoneReminderEmailsToAdmin}
              />
            </VStack>
            <SettingsRow
              id="email-grantDateReminder-enabled"
              label="Grant date reminder emails"
              name="grantDateReminder.enabled"
              description="Send reminders before, on, and after the grant date until the grant is actioned."
              getFieldProps={getFieldProps}
              value={formik.values.grantDateReminder.enabled}
              onToggle={() => {
                formik.setFieldValue(
                  "grantDateReminder.enabled",
                  !formik.values.grantDateReminder.enabled
                );
              }}
              error={
                formik.touched.grantDateReminder?.enabled
                  ? errors?.grantDateReminder?.enabled
                  : undefined
              }
              type="toggle"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendEmailsToAdmin &&
                formik.values.grantDateReminder.enabled
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-grantDateReminder-pre-enabled"
                label="Send pre-grant date reminders"
                name="grantDateReminder.pre.enabled"
                description="Send recurring reminders before the grant date."
                getFieldProps={getFieldProps}
                value={formik.values.grantDateReminder.pre.enabled}
                onToggle={() => {
                  formik.setFieldValue(
                    "grantDateReminder.pre.enabled",
                    !formik.values.grantDateReminder.pre.enabled
                  );
                }}
                error={
                  formik.touched.grantDateReminder?.pre?.enabled
                    ? errors?.grantDateReminder?.pre?.enabled
                    : undefined
                }
                type="toggle"
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.grantDateReminder.enabled
                }
              />
              <VStack
                className={`gap-4 pl-10 ${
                  formik.values.grantDateReminder.pre.enabled
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-grantDateReminder-pre-startFromDays"
                  label="Start reminders (days before grant date)"
                  value={formik.values.grantDateReminder.pre.startFromDays ?? 0}
                  getFieldProps={getFieldProps}
                  error={
                    formik.touched.grantDateReminder?.pre?.startFromDays
                      ? errors?.grantDateReminder?.pre?.startFromDays
                      : undefined
                  }
                  name="grantDateReminder.pre.startFromDays"
                  description="Number of days before the grant date when reminders should start."
                  type="input"
                  inputType="number"
                  disabled={
                    !formik.values.sendEmailsToAdmin ||
                    !formik.values.grantDateReminder.enabled ||
                    !formik.values.grantDateReminder.pre.enabled
                  }
                  required={formik.values.grantDateReminder.pre.enabled}
                />
                <SettingsRow
                  id="email-grantDateReminder-pre-intervalDays"
                  label="Pre-grant reminder interval"
                  value={formik.values.grantDateReminder.pre.intervalDays ?? 0}
                  getFieldProps={getFieldProps}
                  error={
                    formik.touched.grantDateReminder?.pre?.intervalDays
                      ? errors?.grantDateReminder?.pre?.intervalDays
                      : undefined
                  }
                  name="grantDateReminder.pre.intervalDays"
                  description="How frequently reminders should be sent before the grant date, in days."
                  type="input"
                  inputType="number"
                  disabled={
                    !formik.values.sendEmailsToAdmin ||
                    !formik.values.grantDateReminder.enabled ||
                    !formik.values.grantDateReminder.pre.enabled
                  }
                  required={formik.values.grantDateReminder.pre.enabled}
                />
              </VStack>
              <SettingsRow
                id="email-grantDateReminder-due-enabled"
                label="Send reminder on grant date"
                name="grantDateReminder.due.enabled"
                description="Send one reminder on the grant date, even if it does not fall on the pre-grant reminder interval."
                getFieldProps={getFieldProps}
                value={formik.values.grantDateReminder.due.enabled}
                onToggle={() => {
                  formik.setFieldValue(
                    "grantDateReminder.due.enabled",
                    !formik.values.grantDateReminder.due.enabled
                  );
                }}
                error={
                  formik.touched.grantDateReminder?.due?.enabled
                    ? errors?.grantDateReminder?.due?.enabled
                    : undefined
                }
                type="toggle"
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.grantDateReminder.enabled
                }
              />
              <SettingsRow
                id="email-grantDateReminder-post-enabled"
                label="Send post-grant date reminders"
                name="grantDateReminder.post.enabled"
                description="Continue reminders for grants that remain pending after their grant date."
                getFieldProps={getFieldProps}
                value={formik.values.grantDateReminder.post.enabled}
                onToggle={() => {
                  formik.setFieldValue(
                    "grantDateReminder.post.enabled",
                    !formik.values.grantDateReminder.post.enabled
                  );
                }}
                error={
                  formik.touched.grantDateReminder?.post?.enabled
                    ? errors?.grantDateReminder?.post?.enabled
                    : undefined
                }
                type="toggle"
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.grantDateReminder.enabled
                }
              />
              <VStack
                className={`gap-4 pl-10 ${
                  formik.values.grantDateReminder.post.enabled
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-grantDateReminder-post-startFromDays"
                  label="Start post-grant reminders (days after grant date)"
                  value={
                    formik.values.grantDateReminder.post.startFromDays ?? 0
                  }
                  getFieldProps={getFieldProps}
                  error={
                    formik.touched.grantDateReminder?.post?.startFromDays
                      ? errors?.grantDateReminder?.post?.startFromDays
                      : undefined
                  }
                  name="grantDateReminder.post.startFromDays"
                  description="Number of days after the grant date when reminders should start."
                  type="input"
                  inputType="number"
                  disabled={
                    !formik.values.sendEmailsToAdmin ||
                    !formik.values.grantDateReminder.enabled ||
                    !formik.values.grantDateReminder.post.enabled
                  }
                  required={formik.values.grantDateReminder.post.enabled}
                />
                <SettingsRow
                  id="email-grantDateReminder-post-intervalDays"
                  label="Post-grant reminder interval"
                  value={formik.values.grantDateReminder.post.intervalDays ?? 0}
                  getFieldProps={getFieldProps}
                  error={
                    formik.touched.grantDateReminder?.post?.intervalDays
                      ? errors?.grantDateReminder?.post?.intervalDays
                      : undefined
                  }
                  name="grantDateReminder.post.intervalDays"
                  description="How frequently reminders should be sent after the grant date, in days."
                  type="input"
                  inputType="number"
                  disabled={
                    !formik.values.sendEmailsToAdmin ||
                    !formik.values.grantDateReminder.enabled ||
                    !formik.values.grantDateReminder.post.enabled
                  }
                  required={formik.values.grantDateReminder.post.enabled}
                />
              </VStack>
              <SettingsRow
                id="email-grantDateReminder-recipientRoles"
                label="Recipient roles"
                description="Select the roles that should receive the upcoming grant date report."
                value={(formik.values.grantDateReminder.recipientRoles ?? [])
                  .map((value) => upcomingGrantDateValueToLabel[value])
                  .filter((label) => label !== undefined)}
                onChange={(selectedLabels: any[]) => {
                  const roleValues = selectedLabels
                    .map((label) => upcomingGrantDateLabelToValue[label])
                    .filter((value) => value !== undefined);
                  formik.setFieldValue(
                    "grantDateReminder.recipientRoles",
                    roleValues
                  );
                }}
                error={
                  formik.touched.grantDateReminder?.recipientRoles
                    ? Array.isArray(errors?.grantDateReminder?.recipientRoles)
                      ? errors.grantDateReminder?.recipientRoles?.[0]
                      : errors?.grantDateReminder?.recipientRoles
                    : undefined
                }
                name="grantDateReminder.recipientRoles"
                type="multiselect"
                textGetter={(option: any) => option.label}
                valueGetter={(option: any) => option.label}
                options={upcomingGrantDateRecipientRoles.map((role) => ({
                  value: role.label,
                }))}
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.grantDateReminder.enabled
                }
                required={formik.values.grantDateReminder.enabled}
              />
            </VStack>
            <SettingsRow
              id="email-sendTransactionEmailsToAdmin"
              label="Send Transaction related Emails"
              description={`Send Buyback/Exercise/Liquidity emails to admins.`}
              name="sendTransactionEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendTransactionEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendTransactionEmailsToAdmin",
                  !formik.values.sendTransactionEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendResignationReminderEmailsToAdmin"
              label="Upcoming Resignation Reminder Emails"
              description="Enable this option to allow Hissa to send individual resignation reminder emails to admins one day before an employee's last working day"
              name="sendResignationReminderEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendResignationReminderEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendResignationReminderEmailsToAdmin",
                  !formik.values.sendResignationReminderEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendPendingResignationSummaryEmailsToAdmin"
              label="Pending Resignation Summary Emails"
              name="sendPendingResignationSummaryEmailsToAdmin"
              description={`Send periodic summary emails listing employees whose last working date has passed but whose resignation is still incomplete.`}
              getFieldProps={getFieldProps}
              value={formik.values.sendPendingResignationSummaryEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendPendingResignationSummaryEmailsToAdmin",
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                );
              }}
              error={
                formik.touched.sendPendingResignationSummaryEmailsToAdmin
                  ? errors?.sendPendingResignationSummaryEmailsToAdmin
                  : undefined
              }
              type="toggle"
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendPendingResignationSummaryEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-pendingResignationInclusionOffset"
                label="Include Employees After (Days from Last Working Date)"
                value={formik.values.pendingResignationInclusionOffset}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.pendingResignationInclusionOffset
                    ? errors?.pendingResignationInclusionOffset
                    : undefined
                }
                name="pendingResignationInclusionOffset"
                description={`Number of days after an employee’s last working date before they are included in pending resignation list.`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
                required={
                  formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
              />
              <SettingsRow
                id="email-pendingResignationReminderInterval"
                label="Reminder Frequency"
                value={formik.values.pendingResignationReminderInterval}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.pendingResignationReminderInterval
                    ? errors?.pendingResignationReminderInterval
                    : undefined
                }
                name="pendingResignationReminderInterval"
                description={`How frequently the summary emails should be sent (in days)`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
                required={
                  formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
              />
            </VStack>
            <SettingsRow
              id="email-sendOptionsExpiryReminderEmailsToAdmin"
              label="Options Expiry Reminder Emails"
              name="sendOptionsExpiryReminderEmailsToAdmin"
              description={`Send periodic reminder emails to admins for grants nearing options expiry.`}
              getFieldProps={getFieldProps}
              value={formik.values.sendOptionsExpiryReminderEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendOptionsExpiryReminderEmailsToAdmin",
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin
                );
              }}
              error={
                formik.touched.sendOptionsExpiryReminderEmailsToAdmin
                  ? errors?.sendOptionsExpiryReminderEmailsToAdmin
                  : undefined
              }
              type="toggle"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendOptionsExpiryReminderEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-optionsExpiryReminderStartFromAdmin"
                label="Start Reminders (Days Before Expiry)"
                value={formik.values.optionsExpiryReminderStartFromAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.optionsExpiryReminderStartFromAdmin
                    ? errors?.optionsExpiryReminderStartFromAdmin
                    : undefined
                }
                name="optionsExpiryReminderStartFromAdmin"
                description={`Number of days before expiry date when reminders should start`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                required={formik.values.sendOptionsExpiryReminderEmailsToAdmin}
              />
              <SettingsRow
                id="email-optionsExpiryReminderIntervalAdmin"
                label="Reminder Interval"
                value={formik.values.optionsExpiryReminderIntervalAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.optionsExpiryReminderIntervalAdmin
                    ? errors?.optionsExpiryReminderIntervalAdmin
                    : undefined
                }
                name="optionsExpiryReminderIntervalAdmin"
                description={`How frequently the reminder emails should be sent (in days).`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                required={formik.values.sendOptionsExpiryReminderEmailsToAdmin}
              />
            </VStack>
            <SettingsRow
              id="email-sendEmployeeDataSyncRecords"
              label="Send Bi-Monthly Employee Data Sync History Details to Admins"
              description={`Send bi-monthly employee data sync history details to admins.`}
              name="sendEmployeeDataSyncRecords"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendEmployeeDataSyncRecords!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendEmployeeDataSyncRecords",
                  !formik.values.sendEmployeeDataSyncRecords
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            {/* Monthly statement */}
            <SettingsRow
              id="email-sendMonthlyStatement"
              label="Monthly Statement Emails"
              description="Send periodic portfolio statement emails to admins."
              name="sendMonthlyStatement"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendMonthlyStatement ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "sendMonthlyStatement",
                  !formik.values.sendMonthlyStatement
                )
              }
              disabled={!formik.values.sendEmailsToAdmin}
            />

            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendMonthlyStatement
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-monthlyStatementInterval"
                label="Statement Interval"
                description="Number of months each statement covers (1–12 months)."
                value={formik.values.monthlyStatementInterval ?? 1}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.monthlyStatementInterval
                    ? (errors as any)?.monthlyStatementInterval
                    : undefined
                }
                name="monthlyStatementInterval"
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.sendMonthlyStatement
                }
                required={formik.values.sendMonthlyStatement}
              />
              <SettingsRow
                id="email-emailRecipientRoles"
                label="Email Recipient Roles"
                description="Select which user roles should receive emails."
                value={(formik.values.emailRecipientRoles ?? [])
                  .map((value) => valueToLabel[value])
                  .filter((label) => label !== undefined)}
                onChange={(selectedLabels: any[]) => {
                  // Convert label text back to enum values
                  const enumValues = selectedLabels
                    .map((label) => labelToValue[label])
                    .filter((val) => val !== undefined);
                  formik.setFieldValue("emailRecipientRoles", enumValues);
                }}
                error={
                  formik.touched.emailRecipientRoles
                    ? (errors as any)?.emailRecipientRoles
                    : undefined
                }
                name="emailRecipientRoles"
                type="multiselect"
                textGetter={(option: any) => option.label}
                valueGetter={(option: any) => option.label}
                options={emailRecipientRoles.map((role) => ({
                  value: role.label,
                }))}
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.sendMonthlyStatement
                }
              />
            </VStack>
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

const ScheduledEmployeeStatementSettings: React.FC<SubSettingsProps> = ({
  formik,
}) => {
  const { values } = formik;
  const { data: planData } = useEsopPlans();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draft, setDraft] = useState<SendEsopStatementToEmployeeConfig>(
    () => values.sendEsopStatementToEmployeeConfig
  );
  const openedDraftRef = useRef<SendEsopStatementToEmployeeConfig | null>(null);
  const [monthlyDayInput, setMonthlyDayInput] = useState(() =>
    String(
      values.sendEsopStatementToEmployeeConfig.deliverySchedule.dayOfMonth ?? 1
    )
  );
  const [monthlyDayError, setMonthlyDayError] = useState("");
  const disabled = !values.emailsToBeSent || !values.sendEmailsToEmployees;
  const isConfigured = values.sendEsopStatementToEmployeeConfig.isEnabled;
  const configuration = isDialogOpen
    ? draft
    : values.sendEsopStatementToEmployeeConfig;
  const { deliverySchedule, recipientFilters: filters } = configuration;
  const planOptions = useMemo(
    () =>
      (planData ?? [])
        .filter((plan: any) => plan?.planName)
        .map((plan: any) => ({
          id: plan.esopPlanId ?? plan.id,
          name: plan.planName,
        })),
    [planData]
  );

  const openDialog = () => {
    const nextDraft = values.sendEsopStatementToEmployeeConfig;
    openedDraftRef.current = nextDraft;
    setDraft(nextDraft);
    setMonthlyDayInput(String(nextDraft.deliverySchedule.dayOfMonth ?? 1));
    setMonthlyDayError("");
    setIsDialogOpen(true);
  };

  const resetDraft = () => {
    const nextDraft =
      openedDraftRef.current ?? values.sendEsopStatementToEmployeeConfig;
    setDraft(nextDraft);
    setMonthlyDayInput(String(nextDraft.deliverySchedule.dayOfMonth ?? 1));
    setMonthlyDayError("");
  };

  const updateMonthlyDay = (rawValue: string) => {
    setMonthlyDayInput(rawValue);
    const day = Number(rawValue);
    if (
      !/^\d+$/.test(rawValue) ||
      !Number.isInteger(day) ||
      day < 1 ||
      day > 31
    ) {
      setMonthlyDayError("Enter a whole number from 1 to 31.");
      return;
    }

    setMonthlyDayError("");
    setDraft({
      ...draft,
      deliverySchedule: {
        ...draft.deliverySchedule,
        runOn: "DAY_OF_MONTH",
        dayOfMonth: day,
        overflowBehavior:
          day >= 29
            ? draft.deliverySchedule.overflowBehavior ?? "last_day"
            : null,
      },
    });
  };

  const selectDayOfMonthMode = () => {
    setMonthlyDayError("");
    setMonthlyDayInput(String(draft.deliverySchedule.dayOfMonth ?? 1));
    setDraft({
      ...draft,
      deliverySchedule: {
        ...draft.deliverySchedule,
        runOn: "DAY_OF_MONTH",
        dayOfMonth: draft.deliverySchedule.dayOfMonth ?? 1,
        overflowBehavior:
          (draft.deliverySchedule.dayOfMonth ?? 1) >= 29
            ? draft.deliverySchedule.overflowBehavior ?? "last_day"
            : null,
      },
    });
    window.setTimeout(() => {
      document.getElementById("employee-statement-day-of-month")?.focus();
    }, 0);
  };

  const syncDraftToForm = () => {
    formik.setFieldValue("sendEsopStatementToEmployeeConfig", draft);
  };

  useEffect(() => {
    if (isDialogOpen) syncDraftToForm();
  }, [draft, isDialogOpen]);

  const monthlyScheduleDayLabel =
    deliverySchedule.runOn === "LAST_DAY_OF_MONTH"
      ? "last day"
      : `day ${deliverySchedule.dayOfMonth ?? 1}`;
  const scheduleSummary = `Schedule: ${
    deliverySchedule.frequency === "CUSTOM"
      ? `Every ${deliverySchedule.customIntervalDays ?? 30} days`
      : deliverySchedule.frequency === "INTERVAL"
      ? `Every ${
          deliverySchedule.repeatEveryMonths ?? 1
        } months on ${monthlyScheduleDayLabel}`
      : `Monthly on ${monthlyScheduleDayLabel}`
  }`;
  const employmentSummary = filters?.employmentStatuses?.length
    ? filters.employmentStatuses
        .map((status) =>
          status === "EMPLOYED"
            ? "Employed"
            : status === "RESIGNED"
            ? "Resigned"
            : status
        )
        .join(", ")
    : "All employees";
  const grantSummary =
    filters?.grantStates?.length === Object.values(GrantState).length
      ? "All grant states"
      : filters?.grantStates?.length
      ? filters.grantStates.join(", ")
      : "All grant states";
  const planSummary =
    planOptions.length > 0 && filters?.planIds?.length === planOptions.length
      ? "All plans"
      : filters?.planIds?.length
      ? `${filters.planIds.length} selected`
      : "All plans";
  const employmentTypeSummary = filters?.employmentTypes?.length
    ? filters.employmentTypes
        .map((type) =>
          type === "EMPLOYEE"
            ? "Employee"
            : type === "CONSULTANT"
            ? "Consultant"
            : type
        )
        .join(", ")
    : "All employment types";
  const credentialsSummary = filters?.excludeEmployeesWOCredentials
    ? "Generated credentials only"
    : "All credential statuses";

  return (
    <>
      <VStack
        id="email-sendEsopStatementToEmployeeConfig"
        className="w-full gap-3 border-borderColor pt-5"
      >
        <HStack className="w-full items-start justify-between">
          <VStack className="flex-1 gap-1">
            <HStack className="items-center gap-2">
              <span className="text-xs2 font-medium">
                Periodic ESOP Statement Emails
              </span>
            </HStack>
            <span className="text-xs text-gray-500">
              Schedule and send periodic ESOP statement emails to employees.
            </span>
          </VStack>
          <SwitchButton
            value={isConfigured}
            label=""
            disabled={disabled}
            onClick={() => {
              const isEnabled = !isConfigured;
              const nextConfiguration = {
                ...values.sendEsopStatementToEmployeeConfig,
                isEnabled,
              };
              formik.setFieldValue(
                "sendEsopStatementToEmployeeConfig",
                nextConfiguration
              );
              setDraft(nextConfiguration);
              if (!isEnabled) setIsDialogOpen(false);
            }}
          />
        </HStack>
        {isConfigured && (
          <VStack className="gap-0 rounded-md border border-gray-200 bg-white">
            <VStack className="gap-3 p-4">
              <HStack className="items-center justify-between gap-4">
                <HStack className="items-center gap-2">
                  <span className="text-xs2 text-secondaryText">
                    Current Configuration
                  </span>
                </HStack>
                <HStack className="items-center gap-3">
                  {isDialogOpen && (
                    <button
                      type="button"
                      className="text-xs2 font-medium text-secondaryText underline hover:text-primary"
                      onClick={resetDraft}
                    >
                      Reset
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-xs2 font-semibold text-primary"
                    onClick={() => {
                      if (isDialogOpen) {
                        if (monthlyDayError) return;
                        syncDraftToForm();
                        setIsDialogOpen(false);
                      } else openDialog();
                    }}
                  >
                    <span className="flex items-center gap-1 underline">
                      {isDialogOpen
                        ? "Collapse Configuration"
                        : "Configure Settings"}
                      <Icon
                        icon={
                          isDialogOpen
                            ? "material-symbols:keyboard-arrow-up-rounded"
                            : "material-symbols:keyboard-arrow-down-rounded"
                        }
                        width={16}
                        height={16}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </HStack>
              </HStack>
              <VStack className="gap-2 rounded-md border border-gray-200 bg-white p-3 text-xs2 text-primaryText">
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Schedule</span>
                  <span className="font-medium">
                    {scheduleSummary.replace("Schedule: ", "")}
                  </span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Employment</span>
                  <span className="font-medium">{employmentSummary}</span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Type</span>
                  <span className="font-medium">{employmentTypeSummary}</span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Grant states</span>
                  <span className="font-medium">{grantSummary}</span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Plans</span>
                  <span className="font-medium">{planSummary}</span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Credentials</span>
                  <span className="font-medium">{credentialsSummary}</span>
                </HStack>
                <HStack className="gap-2">
                  <span className="w-24 text-secondaryText">Statement</span>
                  <span className="font-medium">
                    Previous {`${configuration.statementLookbackMonths} `}
                    month
                    {configuration.statementLookbackMonths === 1 ? "" : "s"}
                  </span>
                </HStack>
              </VStack>
            </VStack>

            {isDialogOpen && (
              <VStack className="gap-6 border-t border-gray-200 p-5">
                <VStack
                  className={`gap-5 ${draft.isEnabled ? "" : "opacity-50"}`}
                >
                  <HStack className="items-center border-b border-[#DBDFE9] pb-3">
                    <h3 className="text-xs2 font-semibold text-primaryText">
                      SCHEDULE & STATEMENT CADENCE
                    </h3>
                  </HStack>

                  <VStack className="gap-5">
                    <HStack className="gap-5">
                      <VStack
                        className={
                          draft.deliverySchedule.frequency === "CUSTOM"
                            ? "w-[calc(50%-0.625rem)] shrink-0 gap-1"
                            : "flex-1 gap-1"
                        }
                      >
                        <label className="form-label">Delivery Frequency</label>
                        <select
                          className="form-select"
                          value={draft.deliverySchedule.frequency}
                          disabled={!draft.isEnabled}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              deliverySchedule: {
                                ...draft.deliverySchedule,
                                frequency: event.target
                                  .value as SendEsopStatementToEmployeeConfig["deliverySchedule"]["frequency"],
                                firstDeliveryMonth:
                                  event.target.value === "MONTHLY"
                                    ? null
                                    : draft.deliverySchedule.firstDeliveryMonth,
                                repeatEveryMonths:
                                  event.target.value === "INTERVAL"
                                    ? draft.deliverySchedule
                                        .repeatEveryMonths ?? 1
                                    : null,
                                runOn:
                                  event.target.value === "CUSTOM"
                                    ? null
                                    : draft.deliverySchedule.runOn ??
                                      "DAY_OF_MONTH",
                                dayOfMonth:
                                  event.target.value === "CUSTOM"
                                    ? null
                                    : draft.deliverySchedule.dayOfMonth ?? 1,
                                overflowBehavior:
                                  event.target.value === "CUSTOM" ||
                                  (draft.deliverySchedule.dayOfMonth ?? 1) < 29
                                    ? null
                                    : draft.deliverySchedule.overflowBehavior ??
                                      "last_day",
                                customIntervalDays:
                                  event.target.value === "CUSTOM"
                                    ? draft.deliverySchedule
                                        .customIntervalDays ?? 30
                                    : null,
                              },
                            })
                          }
                        >
                          <option value="MONTHLY">Every month</option>
                          <option value="INTERVAL">Every few months</option>
                          <option value="CUSTOM">Custom</option>
                        </select>
                      </VStack>
                      {(draft.deliverySchedule.frequency === "MONTHLY" ||
                        draft.deliverySchedule.frequency === "INTERVAL") && (
                        <VStack className="flex-1 gap-1">
                          <fieldset className="flex flex-col gap-2">
                            <legend className="form-label inline-flex items-center gap-1">
                              Day of Month
                              <Tooltip
                                text={
                                  draft.deliverySchedule.frequency ===
                                  "INTERVAL"
                                    ? "Choose the day for the first delivery month. The statement will run on the same day every selected number of months; set a fallback for days that don't exist in every month."
                                    : "Choose a day (1-31), or the last day of the month. Set a fallback for days that don't exist in every month."
                                }
                                _className="w-[280px]"
                              >
                                <Icon
                                  icon="material-symbols:info-outline-rounded"
                                  width={16}
                                  height={16}
                                  className="cursor-pointer text-secondaryText"
                                />
                              </Tooltip>
                            </legend>
                            <HStack
                              role="group"
                              aria-label="Day of month schedule mode"
                              className="overflow-hidden rounded-lg border border-borderColor"
                            >
                              <HStack
                                className={`flex-1 cursor-pointer items-center gap-2 border-r border-borderColor px-3 py-1 ${
                                  draft.deliverySchedule.runOn ===
                                  "DAY_OF_MONTH"
                                    ? "bg-orange-50"
                                    : "bg-white"
                                }`}
                                onClick={selectDayOfMonthMode}
                              >
                                <button
                                  type="button"
                                  className="text-xs2 font-medium text-primaryText disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-pressed={
                                    draft.deliverySchedule.runOn ===
                                    "DAY_OF_MONTH"
                                  }
                                  disabled={!draft.isEnabled}
                                  onClick={selectDayOfMonthMode}
                                >
                                  On day
                                </button>
                                <Input
                                  id="employee-statement-day-of-month"
                                  className="w-20 min-h-[36px] px-3 py-1.5"
                                  type="number"
                                  min={1}
                                  max={31}
                                  step={1}
                                  inputMode="numeric"
                                  value={monthlyDayInput}
                                  disabled={
                                    !draft.isEnabled ||
                                    draft.deliverySchedule.runOn !==
                                      "DAY_OF_MONTH"
                                  }
                                  aria-invalid={Boolean(monthlyDayError)}
                                  aria-describedby={
                                    monthlyDayError
                                      ? "employee-statement-day-of-month-error"
                                      : undefined
                                  }
                                  aria-label="Day of month"
                                  onChange={(event) =>
                                    updateMonthlyDay(event.target.value)
                                  }
                                />
                              </HStack>
                              <button
                                type="button"
                                className={`flex flex-1 items-center justify-center px-3 py-1 text-xs2 font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                                  draft.deliverySchedule.runOn ===
                                  "LAST_DAY_OF_MONTH"
                                    ? "bg-orange-50 text-primaryText"
                                    : "bg-white text-primaryText"
                                }`}
                                aria-pressed={
                                  draft.deliverySchedule.runOn ===
                                  "LAST_DAY_OF_MONTH"
                                }
                                disabled={!draft.isEnabled}
                                onClick={() => {
                                  setMonthlyDayError("");
                                  setDraft({
                                    ...draft,
                                    deliverySchedule: {
                                      ...draft.deliverySchedule,
                                      runOn: "LAST_DAY_OF_MONTH",
                                      dayOfMonth: null,
                                      overflowBehavior: null,
                                    },
                                  });
                                }}
                              >
                                Last day of month
                              </button>
                            </HStack>
                            {monthlyDayError && (
                              <span
                                id="employee-statement-day-of-month-error"
                                role="alert"
                                className="text-xs1 text-red-500"
                              >
                                {monthlyDayError}
                              </span>
                            )}
                            {draft.deliverySchedule.runOn === "DAY_OF_MONTH" &&
                              (draft.deliverySchedule.dayOfMonth ?? 0) >=
                                29 && (
                                <VStack className="gap-1 border-t border-dashed border-borderColor pt-2">
                                  <label
                                    className="form-label"
                                    htmlFor="employee-statement-overflow-behavior"
                                  >
                                    If this day does not exist in a month, what
                                    should happen?
                                  </label>
                                  <select
                                    id="employee-statement-overflow-behavior"
                                    className="form-select"
                                    value={
                                      draft.deliverySchedule.overflowBehavior ??
                                      "last_day"
                                    }
                                    disabled={!draft.isEnabled}
                                    onChange={(event) =>
                                      setDraft({
                                        ...draft,
                                        deliverySchedule: {
                                          ...draft.deliverySchedule,
                                          overflowBehavior: event.target
                                            .value as NonNullable<
                                            SendEsopStatementToEmployeeConfig["deliverySchedule"]["overflowBehavior"]
                                          >,
                                        },
                                      })
                                    }
                                  >
                                    <option value="last_day">
                                      Run on the last day of the month
                                    </option>
                                    <option value="skip">
                                      Skip that month
                                    </option>
                                    <option value="next_month_first">
                                      Run on the 1st of the next month
                                    </option>
                                  </select>
                                </VStack>
                              )}
                          </fieldset>
                        </VStack>
                      )}
                    </HStack>
                    {draft.deliverySchedule.frequency === "INTERVAL" && (
                      <HStack className="gap-5">
                        <VStack className="flex-1 gap-1">
                          <label className="form-label">
                            First Delivery Month
                          </label>
                          <Input
                            type="month"
                            value={
                              draft.deliverySchedule.firstDeliveryMonth?.slice(
                                0,
                                7
                              ) ?? ""
                            }
                            disabled={!draft.isEnabled}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                deliverySchedule: {
                                  ...draft.deliverySchedule,
                                  firstDeliveryMonth: event.target.value
                                    ? `${event.target.value}-01`
                                    : null,
                                },
                              })
                            }
                          />
                        </VStack>
                        <VStack className="flex-1 gap-1">
                          <label className="form-label">
                            Repeat Every (months)
                          </label>
                          <Input
                            type="number"
                            min={1}
                            max={12}
                            value={
                              draft.deliverySchedule.repeatEveryMonths ?? 1
                            }
                            disabled={!draft.isEnabled}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                deliverySchedule: {
                                  ...draft.deliverySchedule,
                                  repeatEveryMonths: Number(event.target.value),
                                },
                              })
                            }
                          />
                        </VStack>
                      </HStack>
                    )}
                    {draft.deliverySchedule.frequency === "CUSTOM" && (
                      <HStack className="gap-5">
                        <VStack className="flex-1 gap-1">
                          <label className="form-label">Start Date</label>
                          <Input
                            type="date"
                            value={
                              draft.deliverySchedule.firstDeliveryMonth ?? ""
                            }
                            disabled={!draft.isEnabled}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                deliverySchedule: {
                                  ...draft.deliverySchedule,
                                  firstDeliveryMonth:
                                    event.target.value || null,
                                },
                              })
                            }
                          />
                        </VStack>
                        <VStack className="flex-1 gap-1">
                          <label className="form-label">
                            Custom Interval (days)
                          </label>
                          <Input
                            type="number"
                            min={1}
                            value={
                              draft.deliverySchedule.customIntervalDays ?? 30
                            }
                            disabled={!draft.isEnabled}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                deliverySchedule: {
                                  ...draft.deliverySchedule,
                                  customIntervalDays: Number(
                                    event.target.value
                                  ),
                                },
                              })
                            }
                          />
                        </VStack>
                      </HStack>
                    )}
                    <VStack className="w-[calc(50%-0.625rem)] gap-1">
                      <HStack className="items-center gap-1">
                        <label className="form-label mb-0">
                          Statement Interval (months)
                        </label>
                        <Tooltip
                          text="Include data from this many months before each delivery date."
                          _className="w-[280px]"
                        >
                          <Icon
                            icon="material-symbols:info-outline-rounded"
                            width={16}
                            height={16}
                            className="block h-4 w-4 cursor-pointer text-secondaryText"
                          />
                        </Tooltip>
                      </HStack>
                      <Input
                        type="number"
                        min={1}
                        max={120}
                        value={draft.statementLookbackMonths}
                        disabled={!draft.isEnabled}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            statementLookbackMonths: Number(event.target.value),
                          })
                        }
                      />
                    </VStack>

                    <InfoBox
                      message={`Note: Statements will include the previous ${
                        draft.statementLookbackMonths
                      } month${
                        draft.statementLookbackMonths === 1 ? "" : "s"
                      } of data up to the scheduled run date and will be delivered as a PDF file.`}
                    />
                    <HStack className="items-center border-b border-[#DBDFE9] pb-3 pt-2">
                      <h3 className="text-xs2 font-semibold text-primaryText">
                        RECIPIENT & GRANT FILTERS
                      </h3>
                    </HStack>
                    <HStack className="gap-5">
                      <VStack className="flex-1 gap-1">
                        <label className="form-label">
                          Employment Status Filter
                        </label>
                        <select
                          className="form-select"
                          value={
                            draft.recipientFilters.employmentStatuses?.[0] ??
                            "ALL"
                          }
                          disabled={!draft.isEnabled}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              recipientFilters: {
                                ...draft.recipientFilters,
                                employmentStatuses:
                                  event.target.value === "ALL"
                                    ? []
                                    : [event.target.value],
                              },
                            })
                          }
                        >
                          <option value="ALL">All</option>
                          <option value="EMPLOYED">Employed (Active)</option>
                          <option value="RESIGNED">Resigned</option>
                        </select>
                      </VStack>
                      <VStack className="flex-1 gap-1">
                        <label className="form-label">
                          Employment Type Filter
                        </label>
                        <select
                          className="form-select"
                          value={
                            draft.recipientFilters.employmentTypes?.[0] ?? "ALL"
                          }
                          disabled={!draft.isEnabled}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              recipientFilters: {
                                ...draft.recipientFilters,
                                employmentTypes:
                                  event.target.value === "ALL"
                                    ? []
                                    : [event.target.value],
                              },
                            })
                          }
                        >
                          <option value="ALL">All</option>
                          <option value="EMPLOYEE">Employee</option>
                          <option value="CONSULTANT">Consultant</option>
                        </select>
                      </VStack>
                    </HStack>
                    <HStack className="gap-5">
                      <VStack className="flex-1 gap-1">
                        <HStack className="items-center gap-1">
                          <label className="form-label mb-0">
                            Grant Filter
                          </label>
                          <Tooltip
                            text="Employees with any grant in a selected state receive the statement. Employees without a grant in one of those states do not receive it."
                            _className="w-[280px]"
                          >
                            <Icon
                              icon="material-symbols:info-outline-rounded"
                              width={16}
                              height={16}
                              className="block h-4 w-4 cursor-pointer text-secondaryText"
                            />
                          </Tooltip>
                        </HStack>
                        <MultiSelectV2
                          multiple={true}
                          placeholder="All grant states"
                          options={Object.values(GrantState).map((state) => ({
                            value: state,
                          }))}
                          optionValues={
                            draft.recipientFilters.grantStates ?? []
                          }
                          handleSelection={(selectedStates: any[]) =>
                            setDraft({
                              ...draft,
                              recipientFilters: {
                                ...draft.recipientFilters,
                                grantStates: selectedStates,
                              },
                            })
                          }
                          _className="w-full"
                          disabled={!draft.isEnabled}
                        />
                      </VStack>
                      <VStack className="flex-1 gap-1">
                        <HStack className="items-center gap-1">
                          <label className="form-label mb-0">Plan Filter</label>
                          <Tooltip
                            text="Employees with any grant under a selected plan receive the statement. Employees without a grant under one of those plans do not receive it."
                            _className="w-[280px]"
                          >
                            <Icon
                              icon="material-symbols:info-outline-rounded"
                              width={16}
                              height={16}
                              className="block h-4 w-4 cursor-pointer text-secondaryText"
                            />
                          </Tooltip>
                        </HStack>
                        <MultiSelectV2
                          multiple={true}
                          placeholder="All plans"
                          options={planOptions.map((plan) => ({
                            value: plan.name,
                          }))}
                          optionValues={(draft.recipientFilters.planIds ?? [])
                            .map(
                              (planId) =>
                                planOptions.find((plan) => plan.id === planId)
                                  ?.name
                            )
                            .filter((name): name is string => Boolean(name))}
                          handleSelection={(selectedPlanNames: any[]) =>
                            setDraft({
                              ...draft,
                              recipientFilters: {
                                ...draft.recipientFilters,
                                planIds: planOptions
                                  .filter((plan) =>
                                    selectedPlanNames.includes(plan.name)
                                  )
                                  .map((plan) => plan.id),
                              },
                            })
                          }
                          _className="w-full"
                          disabled={!draft.isEnabled}
                        />
                      </VStack>
                    </HStack>
                    <label className="flex w-full items-center gap-3 pt-2 rounded-md text-xs2 text-primaryText">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 cursor-pointer accent-[#E85936] disabled:cursor-not-allowed disabled:opacity-50"
                        checked={Boolean(
                          draft.recipientFilters.excludeEmployeesWOCredentials
                        )}
                        disabled={!draft.isEnabled}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            recipientFilters: {
                              ...draft.recipientFilters,
                              excludeEmployeesWOCredentials:
                                event.target.checked,
                            },
                          })
                        }
                      />
                      Exclude employees whose credentials are not generated
                    </label>
                  </VStack>
                </VStack>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </>
  );
};

export const EmployeeEmailSettings: React.FC<SubSettingsProps> = ({
  formik,
}) => {
  const { getFieldProps, values, touched, errors } = formik;

  const disabledIfParentOff = (parent: boolean, child?: boolean) =>
    !values.sendEmailsToEmployees || !parent || !child;

  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          {/* Root toggle */}
          <SettingsRow
            id="email-sendEmailsToEmployees"
            label="Allow Hissa to send notification Emails to Employees"
            description="This master toggle controls whether any notification emails are sent to employees."
            name="sendEmailsToEmployees"
            type="toggle"
            getFieldProps={getFieldProps}
            value={values.sendEmailsToEmployees}
            onToggle={() =>
              formik.setFieldValue(
                "sendEmailsToEmployees",
                !values.sendEmailsToEmployees
              )
            }
          />

          <VStack
            className={`justify-between gap-5 pl-10 ${
              values.sendEmailsToEmployees
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            {/* Grant related emails */}
            <SettingsRow
              id="email-sendGrantEmailsToEmployees"
              label="Grant related Emails"
              description="Enable grant lifecycle emails for employees."
              name="sendGrantEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendGrantEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendGrantEmailsToEmployees",
                  !values.sendGrantEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendGrantEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendGrantOfferedEmailsToEmployees"
                label="Offered Emails"
                description="Send emails when grants are offered."
                name="sendGrantOfferedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantOfferedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantOfferedEmailsToEmployees",
                    !values.sendGrantOfferedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-generateCredentialsOnGrantOffer"
                label="Generate credentials on offer"
                description="Create portal credentials for employees without access when their grant is offered."
                name="generateCredentialsOnGrantOffer"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.generateCredentialsOnGrantOffer}
                onToggle={() =>
                  formik.setFieldValue(
                    "generateCredentialsOnGrantOffer",
                    !values.generateCredentialsOnGrantOffer
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-isAttachmentEnabled"
                label="Offer email attachments"
                description="Allow Employees to receive attachments in the offer letter"
                name="isAttachmentEnabled"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.isAttachmentEnabled}
                onToggle={() =>
                  formik.setFieldValue(
                    "isAttachmentEnabled",
                    !values.isAttachmentEnabled
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendGrantWithdrawEmailsToEmployees"
                label="Withdrawn Emails"
                description="Send emails when grant offers are withdrawn."
                name="sendGrantWithdrawEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantWithdrawEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantWithdrawEmailsToEmployees",
                    !values.sendGrantWithdrawEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendGrantReminderEmailsToEmployees"
                label="Grant Acceptance Reminder Emails"
                description="Send periodic reminder emails to employees to accept their pending grant offer."
                name="sendGrantReminderEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantReminderEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantReminderEmailsToEmployees",
                    !values.sendGrantReminderEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <VStack
                className={`gap-15 pl-10 ${
                  values.sendGrantReminderEmailsToEmployees
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <HStack className="items-center justify-between gap-4">
                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps("grantReminderStartFromEmployee")}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendGrantReminderEmailsToEmployees
                        }
                      />
                      {touched?.grantReminderStartFromEmployee &&
                      (errors as any)?.grantReminderStartFromEmployee ? (
                        <Error
                          text={(errors as any)?.grantReminderStartFromEmployee}
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p
                      className={`text-sm font-medium form-label ${
                        values.sendGrantReminderEmailsToEmployees
                          ? "required"
                          : ""
                      }`}
                    >
                      Days after grant offer
                    </p>
                  </HStack>

                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps("grantReminderIntervalEmployee")}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendGrantReminderEmailsToEmployees
                        }
                      />
                      {touched?.grantReminderIntervalEmployee &&
                      (errors as any)?.grantReminderIntervalEmployee ? (
                        <Error
                          text={(errors as any)?.grantReminderIntervalEmployee}
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p
                      className={`text-sm font-medium form-label ${
                        values.sendGrantReminderEmailsToEmployees
                          ? "required"
                          : ""
                      }`}
                    >
                      Interval Days
                    </p>
                  </HStack>
                </HStack>
              </VStack>

              <SettingsRow
                id="email-sendOptionsExpiryReminderEmailsToEmployee"
                label="Options Expiry Reminder Emails (Employees)"
                description="Send periodic reminder emails to employees for grants nearing options expiry."
                name="sendOptionsExpiryReminderEmailsToEmployee"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendOptionsExpiryReminderEmailsToEmployee}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendOptionsExpiryReminderEmailsToEmployee",
                    !values.sendOptionsExpiryReminderEmailsToEmployee
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <VStack
                className={`gap-15 pl-10 ${
                  values.sendOptionsExpiryReminderEmailsToEmployee
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <HStack className="items-center justify-between gap-4">
                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps(
                          "optionsExpiryReminderStartFromEmployee"
                        )}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendOptionsExpiryReminderEmailsToEmployee
                        }
                      />
                      {touched?.optionsExpiryReminderStartFromEmployee &&
                      (errors as any)
                        ?.optionsExpiryReminderStartFromEmployee ? (
                        <Error
                          text={
                            (errors as any)
                              ?.optionsExpiryReminderStartFromEmployee
                          }
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p
                      className={`text-sm font-medium form-label ${
                        values.sendOptionsExpiryReminderEmailsToEmployee
                          ? "required"
                          : ""
                      }`}
                    >
                      Days before expiry
                    </p>
                  </HStack>

                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps(
                          "optionsExpiryReminderIntervalEmployee"
                        )}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendOptionsExpiryReminderEmailsToEmployee
                        }
                      />
                      {touched?.optionsExpiryReminderIntervalEmployee &&
                      (errors as any)?.optionsExpiryReminderIntervalEmployee ? (
                        <Error
                          text={
                            (errors as any)
                              ?.optionsExpiryReminderIntervalEmployee
                          }
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p
                      className={`text-sm font-medium form-label ${
                        values.sendOptionsExpiryReminderEmailsToEmployee
                          ? "required"
                          : ""
                      }`}
                    >
                      Interval Days
                    </p>
                  </HStack>
                </HStack>
              </VStack>

              <SettingsRow
                id="email-sendGrantAcceptedEmailsToEmployees"
                label="Grant accepted emails with signed grant attached"
                description="Send email with signed grant attached when employee accepts the grant"
                name="sendGrantAcceptedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantAcceptedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantAcceptedEmailsToEmployees",
                    !values.sendGrantAcceptedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />
            </VStack>

            {/* Vesting related emails */}
            <SettingsRow
              id="email-sendVestingEmailsToEmployees"
              label="Vesting related Emails to Employees"
              description="Enable vesting updates for employees."
              name="sendVestingEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendVestingEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendVestingEmailsToEmployees",
                  !values.sendVestingEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendVestingEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendVestingCompleteEmailsToEmployees"
                label="Vesting Completion Emails"
                description="Notify employees when a vesting schedule completes."
                name="sendVestingCompleteEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendVestingCompleteEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendVestingCompleteEmailsToEmployees",
                    !values.sendVestingCompleteEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendVestingEmailsToEmployees
                }
              />
              <VStack
                className={`justify-between gap-5 pl-10 ${
                  values.sendVestingCompleteEmailsToEmployees
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-sendZeroVestingEmailsToEmployees"
                  label="Zero Vesting Emails"
                  description="Notify employees even when their grants have zero vesting."
                  name="sendZeroVestingEmailsToEmployees"
                  type="toggle"
                  getFieldProps={getFieldProps}
                  value={values.sendZeroVestingEmailsToEmployees ?? false}
                  onToggle={() =>
                    formik.setFieldValue(
                      "sendZeroVestingEmailsToEmployees",
                      !values.sendZeroVestingEmailsToEmployees
                    )
                  }
                  disabled={
                    !values.sendEmailsToEmployees ||
                    !values.sendVestingEmailsToEmployees ||
                    !values.sendVestingCompleteEmailsToEmployees
                  }
                />
              </VStack>
            </VStack>
            {/* Buyback related emails */}
            <SettingsRow
              id="email-sendBuyBackEmailsToEmployees"
              label="Buyback related Emails"
              description="Enable buyback lifecycle emails for employees."
              name="sendBuyBackEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendBuyBackEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendBuyBackEmailsToEmployees",
                  !values.sendBuyBackEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendBuyBackEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendBuyBackOfferedEmailsToEmployees"
                label="Offered Emails"
                description="Send emails when buyback offers are available."
                name="sendBuyBackOfferedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackOfferedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackOfferedEmailsToEmployees",
                    !values.sendBuyBackOfferedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendBuyBackClosedEmailsToEmployees"
                label="Closed or Completion Emails to Employees"
                description="Notify employees when a buyback closes or completes."
                name="sendBuyBackClosedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackClosedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackClosedEmailsToEmployees",
                    !values.sendBuyBackClosedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />

              <VStack
                className={`justify-between pl-10 ${
                  values.sendBuyBackClosedEmailsToEmployees
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-sendForfeitureLeterToEmployees"
                  label="Allow Employees to receive forfeiture letter as attachment"
                  description="Attach forfeiture letters to the relevant buyback completion emails."
                  name="sendForfeitureLeterToEmployees"
                  type="toggle"
                  getFieldProps={getFieldProps}
                  value={values.sendForfeitureLeterToEmployees}
                  onToggle={() =>
                    formik.setFieldValue(
                      "sendForfeitureLeterToEmployees",
                      !values.sendForfeitureLeterToEmployees
                    )
                  }
                  disabled={
                    !values.sendEmailsToEmployees ||
                    !values.sendBuyBackClosedEmailsToEmployees
                  }
                />
              </VStack>

              <SettingsRow
                id="email-sendBuyBackReminderEmailsToEmployees"
                label="Buyback Acceptance Reminder Emails"
                description="Send periodic reminder emails to employees to accept their pending buyback offer."
                name="sendBuyBackReminderEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackReminderEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackReminderEmailsToEmployees",
                    !values.sendBuyBackReminderEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />
            </VStack>

            {/* Full and Final letter */}
            <SettingsRow
              id="email-sendFullAndFinalLetterToEmployees"
              label="Full and Final Letter Emails"
              description="Send a notification email to employees upon separation, requesting their acknowledgement and signature on the full and final settlement letter."
              name="sendFullAndFinalLetterToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendFullAndFinalLetterToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendFullAndFinalLetterToEmployees",
                  !values.sendFullAndFinalLetterToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />
            <ScheduledEmployeeStatementSettings formik={formik} />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

const ManagerEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values } = formik;

  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          <SettingsRow
            id="email-sendEmailsToManagers"
            label="Allow Hissa to send notification Emails to Managers"
            description="This master toggle controls whether any notification emails are sent to managers."
            name="sendEmailsToManagers"
            type="toggle"
            getFieldProps={getFieldProps}
            value={values.sendEmailsToManagers}
            onToggle={() =>
              formik.setFieldValue(
                "sendEmailsToManagers",
                !values.sendEmailsToManagers
              )
            }
          />

          <VStack
            className={`justify-between gap-5 pl-10 ${
              values.sendEmailsToManagers ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            <SettingsRow
              id="email-sendManagerPortalAccessEmailToManagers"
              label="Manager Portal Access Granted Emails"
              description="Notify a manager when they are granted Manager Portal access for the first time."
              name="sendManagerPortalAccessEmailToManagers"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendManagerPortalAccessEmailToManagers}
              onToggle={() =>
                formik.setFieldValue(
                  "sendManagerPortalAccessEmailToManagers",
                  !values.sendManagerPortalAccessEmailToManagers
                )
              }
              disabled={!values.sendEmailsToManagers}
            />

            <SettingsRow
              id="email-sendManagerEmployeeAssignedEmailToManagers"
              label="Employee Assigned Emails"
              description="Notify a manager when new employees are added to their team."
              name="sendManagerEmployeeAssignedEmailToManagers"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendManagerEmployeeAssignedEmailToManagers}
              onToggle={() =>
                formik.setFieldValue(
                  "sendManagerEmployeeAssignedEmailToManagers",
                  !values.sendManagerEmployeeAssignedEmailToManagers
                )
              }
              disabled={!values.sendEmailsToManagers}
            />

            <SettingsRow
              id="email-sendManagerEmployeeRemovedEmailToManagers"
              label="Employee Removed Emails"
              description="Notify a manager when an employee is removed from their team."
              name="sendManagerEmployeeRemovedEmailToManagers"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendManagerEmployeeRemovedEmailToManagers}
              onToggle={() =>
                formik.setFieldValue(
                  "sendManagerEmployeeRemovedEmailToManagers",
                  !values.sendManagerEmployeeRemovedEmailToManagers
                )
              }
              disabled={!values.sendEmailsToManagers}
            />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

export default EmailSettings;
